import merge from "lodash/merge";
import { AppliedProfileTypes } from "@enums/applied-profile-types";
import { AvailablePropertyTypes } from "@enums/available-property-types";
import { FormattedAppliedProfile } from "@idw-types/data/applied-profile/applied-profile";
import { FormattedRpServiceConfigurationProperty } from "@idw-types/data/rp-service-configuration";
import { isNullOrUndefined } from "./object-util";

export const sortAppliedProfiles = (
    firstProfile: { type: AppliedProfileTypes; precedence: number; typeValue: number },
    secondProfile: { type: AppliedProfileTypes; precedence: number; typeValue: number }
): number => {
    if (firstProfile.type === secondProfile.type) {
        return firstProfile.precedence - secondProfile.precedence;
    } else {
        return firstProfile.typeValue - secondProfile.typeValue;
    }
};

export const getRpServicePropertyValueOrAppliedProfilePropertyValue = (
    properties: Array<FormattedRpServiceConfigurationProperty>,
    appliedProfiles: Array<FormattedAppliedProfile>,
    propertyTypeId: string
) => {
    const property = properties.find(property => property.propertyTypeId === propertyTypeId && property.addedToService);
    if (property) {
        if (property.type === AvailablePropertyTypes.JSON) {
            try {
                const value = JSON.parse(property.value);
                return value;
            } catch (error) {
                console.error(error);
            }
        } else {
            return property.value;
        }

    } else {
        return getPropertyValuesPassedToService(appliedProfiles, propertyTypeId);
    }
};

export const getProfilePropertiesForPropertyTypeId = (appliedProfiles: Array<FormattedAppliedProfile>, propertyTypeId: string) => {
    const specificProfileProperties = appliedProfiles
        // filter out profiles that dont have the specified property
        .filter(appliedProfile => appliedProfile.profile.profileProperties.some(property =>
            property.availableProperty?.id === propertyTypeId &&
            property.addedToProfile &&
            !isNullOrUndefined(property.value)
        ))
        // sort the profiles by type and precedence
        .sort((a, b) => sortAppliedProfiles(a, b))
        //grab the value, profile type, profile precedence, and profile name
        .map(appliedProfile => ({
            propertyValue: appliedProfile.profile.profileProperties.find(property => property.availableProperty?.id === propertyTypeId).value,
            propertyType: appliedProfile.profile.profileProperties.find(property => property.availableProperty?.id === propertyTypeId).availableProperty.type,
            profileType: appliedProfile.type,
            profileTypeValue: appliedProfile.typeValue,
            profilePrecedence: appliedProfile.precedence,
            profileName: appliedProfile.profile.name,
            profileId: appliedProfile.id
        }));
    return specificProfileProperties;
};

export const getProfilePropertyValuesForPropertyTypeId = (appliedProfiles: Array<FormattedAppliedProfile>, propertyTypeId: string) => {
    const specificProfileProperties = getProfilePropertiesForPropertyTypeId(appliedProfiles, propertyTypeId);

    const parsedProperties: Array<any> = [];
    specificProfileProperties.forEach(profileProperty => {
        if (profileProperty.propertyType === AvailablePropertyTypes.JSON) {
            try {
                const parsedProperty = JSON.parse(profileProperty.propertyValue);
                parsedProperties.push(parsedProperty);
            } catch (error) {
                console.error(error);
            }
        } else {
            parsedProperties.push(profileProperty.propertyValue);
        }
    });

    return parsedProperties;
};

export const getProfilePropertyValuesWithProfileIdForPropertyTypeId = (appliedProfiles: Array<FormattedAppliedProfile>, propertyTypeId: string) => {
    const specificProfileProperties = getProfilePropertiesForPropertyTypeId(appliedProfiles, propertyTypeId);

    const parsedProperties: Array<{
        profileId: string;
        profileType: AppliedProfileTypes;
        value: any;
    }> = [];

    specificProfileProperties.forEach(profileProperty => {
        if (profileProperty.propertyType === AvailablePropertyTypes.JSON) {
            try {
                const parsedProperty = JSON.parse(profileProperty.propertyValue);
                parsedProperties.push({
                    profileId: profileProperty.profileId,
                    profileType: profileProperty.profileType,
                    value: parsedProperty
                });
            } catch (error) {
                console.error(error);
            }
        } else {
            parsedProperties.push({
                profileId: profileProperty.profileId,
                profileType: profileProperty.profileType,
                value: profileProperty.propertyValue
            });
        }
    });

    return parsedProperties;
};

export const getPropertyValuesPassedToServiceWithDefault = (appliedProfiles: Array<FormattedAppliedProfile>, defaultValue: any, propertyTypeId: string) => {
    const profilePropertyValues = getProfilePropertyValuesForPropertyTypeId(appliedProfiles, propertyTypeId);
    if (profilePropertyValues.length > 0) {
        return merge({}, defaultValue, ...profilePropertyValues);
    }
    return defaultValue;
};

export const getPropertyValuesPassedToService = (appliedProfiles: Array<FormattedAppliedProfile>, propertyTypeId: string): string => {
    const profilePropertyValues = getProfilePropertyValuesForPropertyTypeId(appliedProfiles, propertyTypeId);
    if (profilePropertyValues.length > 0) {
        return profilePropertyValues[profilePropertyValues.length - 1];
    }
    return null;
};

export const convertAppliedProfileTypeToValue = (type: AppliedProfileTypes) => {
    switch (type) {
        case AppliedProfileTypes.IDW:
            return 0;
        case AppliedProfileTypes.COMPANY:
            return 10;
        case AppliedProfileTypes.RELYING_PARTY:
            return 20;
        case AppliedProfileTypes.TEMPLATE:
            return 30;
        case AppliedProfileTypes.WORKFLOW:
            return 40;
        case AppliedProfileTypes.SERVICE:
            return 50;
        default:
            return 100;
    }
};